import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Edit3, Trash2, Check, X, Plus, Info, Search, Loader2 } from 'lucide-react';
import { RecipeDetails } from '@/types/recipe-details';
import {
  customizeFinalizedMealPlanItem,
  getCustomizedRecipeByItem,
  getIngredientNutrition,
  getIngredientNutritionByIngredientId,
  getIngredientNutritionByRecipeIngredientId,
  getCustomizedRecipeDetails,
  getFinalizedMealPlanItemIngredients,
  removeDraftRecipeIngredient,
  updateRecipeCustomizations,
} from '@/lib/apiService';
import { extractErrorMessage } from '@/utils/toast-utils';
import { useDebouncedIngredientSearch } from '@/hooks/useDebouncedIngredientSearch';
import { useSpoonacularTagEnrichment } from '@/hooks/useSpoonacularTagEnrichment';
import { IngredientModification } from '@/types/meal-plan';
import { mealKeysMatch } from '@/utils/meal-type-utils';
import { formatGramMlWeightLabel, formatMacro, resolveGramsPerMl } from '@/utils/gram-ml';
import { extractIngredientUnitMetadata } from '@/utils/ingredient-units';
import { formatIngredientDisplayName } from '@/lib/utils';
import { IngredientList, type Ingredient as RecipeIngredientListItem } from '@/components/recipes/IngredientList';
import { RecipeAllergenCard } from '@/components/recipes/RecipeAllergenCard';
import { SpoonacularTagSummary } from '@/components/recipes/SpoonacularTagSummary';
import RecipeServingInfoPopover, { getRecipeServingDisplayValue } from './RecipeServingInfoPopover';
import type { RecipeServingSize } from '@/types/custom-recipe';
import InlineFeedback from '@/components/shared/InlineFeedback';

/** Ingredient shape used in this component's local state */
interface LocalIngredient {
  id: string;
  ingredientId?: string;
  originalIngredientId?: string;
  recipeIngredientId?: string;
  source?: string;
  provider?: string;
  overrideAction?: string;
  isAdded?: boolean;
  name: string;
  amount: number;
  unit: string;
  originalString: string;
  nutrition?: Record<string, unknown>;
}

type IngredientSearchResult = {
  id?: string;
  name: string;
  description?: string | null;
  image?: string;
  imageUrl?: string;
  aisle?: string;
  allergens?: string[];
  [key: string]: unknown;
};

const getNumericNutritionValue = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  if (value && typeof value === 'object') {
    const quantity = (value as { quantity?: unknown }).quantity;
    if (quantity !== undefined) {
      return getNumericNutritionValue(quantity);
    }
  }
  return null;
};

const pickNutritionValue = (...values: unknown[]): number => {
  for (const value of values) {
    const parsed = getNumericNutritionValue(value);
    if (parsed !== null) return parsed;
  }
  return 0;
};

const normalizeUnits = (units: unknown[]): string[] =>
  [...new Set(
    units
      .map((unit) => String(unit ?? '').trim())
      .filter((unit) => unit.length > 0)
  )];

const normalizeUnitKey = (unit: string): string => unit.trim().toLowerCase();

const asRecord = (value: unknown): Record<string, unknown> | undefined =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;

type IngredientNutritionSource = 'all' | 'spoonacular' | 'edamam' | 'internal';

const normalizeIngredientNutritionSource = (
  value: unknown,
  fallback: IngredientNutritionSource = 'all'
): IngredientNutritionSource => {
  const normalized = String(value ?? '').trim().toLowerCase();
  if (normalized === 'spoonacular') return 'spoonacular';
  if (normalized === 'edamam') return 'edamam';
  if (
    normalized === 'internal'
    || normalized === 'cached'
    || normalized === 'custom'
    || normalized === 'manual'
    || normalized === 'caloriescience'
  ) {
    return 'internal';
  }
  return fallback;
};

/** Inline result thumbnail — matches create-recipe `IngredientForm` styling. */
function AddIngredientResultThumbnail({
  imageUrl,
  name,
}: {
  imageUrl?: string;
  name: string;
}) {
  return (
    <div className="size-[44px] min-w-[44px] shrink-0 overflow-hidden rounded-[14px] border border-[#f3f4f6] bg-white p-0.5 shadow-sm">
      {imageUrl ? (
        <img src={imageUrl} alt="" className="h-full w-full rounded-[12px] object-contain" />
      ) : (
        <div className="flex h-full w-full items-center justify-center rounded-[12px] bg-[#f3f4f6] text-sm font-semibold text-[#99a1af]">
          {name.trim().slice(0, 1).toUpperCase() || '?'}
        </div>
      )}
    </div>
  );
}

const mapIngredientNutritionToUi = (nutrition: Record<string, unknown>) => {
  const macros = (nutrition.macros as Record<string, unknown> | undefined) ?? {};
  const micros = (nutrition.micros as Record<string, unknown> | undefined) ?? {};
  const vitamins = (micros.vitamins as Record<string, unknown> | undefined) ?? {};
  const minerals = (micros.minerals as Record<string, unknown> | undefined) ?? {};

  return {
    calories: pickNutritionValue(nutrition.calories, macros.calories),
    protein: pickNutritionValue(nutrition.protein, macros.protein),
    carbs: pickNutritionValue(nutrition.carbohydrate, nutrition.carbs, macros.carbs, macros.carbohydrate),
    fat: pickNutritionValue(nutrition.total_fat, nutrition.fat, macros.fat),
    fiber: pickNutritionValue(nutrition.dietary_fibre, nutrition.fiber, macros.fiber),
    sugar: pickNutritionValue(nutrition.sugar, macros.sugar),
    sodium: pickNutritionValue(nutrition.sodium, macros.sodium),
    cholesterol: pickNutritionValue(nutrition.cholesterol, macros.cholesterol),
    saturatedFat: pickNutritionValue(nutrition.saturated_fat, nutrition.saturatedFat, macros.saturatedFat),
    transFat: pickNutritionValue(nutrition.trans_fat, nutrition.transFat, macros.transFat),
    monounsaturatedFat: pickNutritionValue(
      nutrition.mufa,
      nutrition.monounsaturated_fat,
      nutrition.monounsaturatedFat,
      macros.monounsaturatedFat
    ),
    polyunsaturatedFat: pickNutritionValue(
      nutrition.pufa,
      nutrition.polyunsaturated_fat,
      nutrition.polyunsaturatedFat,
      macros.polyunsaturatedFat
    ),
    vitaminA: pickNutritionValue(nutrition.vitamin_a, vitamins.vitaminA),
    vitaminC: pickNutritionValue(nutrition.vitamin_c, vitamins.vitaminC),
    vitaminD: pickNutritionValue(nutrition.vitamin_d, vitamins.vitaminD),
    vitaminE: pickNutritionValue(nutrition.vitamin_e, vitamins.vitaminE),
    vitaminK: pickNutritionValue(nutrition.vitamin_k, vitamins.vitaminK),
    thiamin: pickNutritionValue(nutrition.vitamin_b1, vitamins.thiamin),
    riboflavin: pickNutritionValue(nutrition.vitamin_b2, vitamins.riboflavin),
    niacin: pickNutritionValue(nutrition.vitamin_b3, vitamins.niacin),
    vitaminB6: pickNutritionValue(nutrition.vitamin_b6, vitamins.vitaminB6),
    folate: pickNutritionValue(nutrition.vitamin_b9, vitamins.folate),
    vitaminB12: pickNutritionValue(nutrition.vitamin_b12, vitamins.vitaminB12),
    biotin: pickNutritionValue(nutrition.biotin, vitamins.biotin),
    pantothenicAcid: pickNutritionValue(nutrition.vitamin_b5, vitamins.pantothenicAcid),
    calcium: pickNutritionValue(nutrition.calcium, minerals.calcium),
    iron: pickNutritionValue(nutrition.iron, minerals.iron),
    magnesium: pickNutritionValue(nutrition.magnesium, minerals.magnesium),
    phosphorus: pickNutritionValue(nutrition.phosphorus, minerals.phosphorus),
    potassium: pickNutritionValue(nutrition.potassium, minerals.potassium),
    zinc: pickNutritionValue(nutrition.zinc, minerals.zinc),
    copper: pickNutritionValue(nutrition.copper, minerals.copper),
    manganese: pickNutritionValue(nutrition.manganese, minerals.manganese),
    selenium: pickNutritionValue(nutrition.selenium, minerals.selenium),
    iodine: pickNutritionValue(nutrition.iodine, minerals.iodine),
    chromium: pickNutritionValue(nutrition.chromium, minerals.chromium),
    molybdenum: pickNutritionValue(nutrition.molybdenum, minerals.molybdenum),
  };
};

function getRemovedIngredientIds(customization: unknown): Set<string> {
  const custom = customization as Record<string, unknown> | undefined;
  const overrides = custom?.ingredientOverrides;
  if (!Array.isArray(overrides)) return new Set<string>();

  return new Set(
    overrides
      .map((override) => override as Record<string, unknown>)
      .filter((override) => String(override.action).toLowerCase() === 'remove')
      .map((override) => String(override.originalIngredientId ?? '').trim())
      .filter((id) => id.length > 0)
  );
}

function ingredientHasRemovedId(ingredient: Record<string, unknown>, removedIds: Set<string>): boolean {
  if (removedIds.size === 0) return false;
  const candidateIds = [
    ingredient.recipeIngredientId,
    ingredient.ingredientId,
    ingredient.id,
    ingredient._id,
  ]
    .map((value) => String(value ?? '').trim())
    .filter((value) => value.length > 0);

  return candidateIds.some((candidateId) => removedIds.has(candidateId));
}

/** Read ingredients from API response: prefer customization.finalIngredients (updated after modifications). */
function getIngredientsFromResponse(data: unknown): LocalIngredient[] | null {
  const d = data as Record<string, unknown> | null | undefined;
  const custom = d?.customization as Record<string, unknown> | undefined;
  const removedIds = getRemovedIngredientIds(custom);
  const recipe = d?.recipe as Record<string, unknown> | undefined;
  const candidates: unknown[] = [
    custom?.finalIngredients,
    custom?.ingredients,
    d?.ingredients,
    recipe?.ingredients,
  ];
  const sourceIngredients = candidates.find((candidate) => Array.isArray(candidate)) as unknown[] | undefined;
  if (Array.isArray(sourceIngredients)) {
    return sourceIngredients
      .map((ing) => ing as Record<string, unknown>)
      .filter((item) => !ingredientHasRemovedId(item, removedIds))
      .map((item: Record<string, unknown>, idx: number) => {
        const recipeIngredientId = item.recipeIngredientId as string | undefined;
        const otherId =
          (item.ingredientId as string) ??
          (item.id as string) ??
          (item._id as string);
        const ingredientId = recipeIngredientId ?? otherId ?? undefined;
        const catalogIngredientId =
          typeof item.ingredientId === 'string' && item.ingredientId.trim().length > 0
            ? item.ingredientId.trim()
            : undefined;
        const amount = Number(item.amount ?? item.quantity) || 0;
        const unit = (item.unit as string) ?? (item.servingId as string) ?? '';
        const name = (item.name as string) ?? '';
        const overrideAction = typeof item.overrideAction === 'string' ? item.overrideAction : undefined;
        const normalizedOverrideAction = (overrideAction ?? '').trim().toLowerCase();
        return {
          id: ingredientId ?? `ingredient-${Date.now()}-${idx}`,
          ingredientId: catalogIngredientId,
          originalIngredientId: ingredientId,
          recipeIngredientId,
          source: typeof item.source === 'string' ? item.source : undefined,
          provider: typeof item.provider === 'string' ? item.provider : undefined,
          overrideAction,
          isAdded: Boolean(item.isAdded) || normalizedOverrideAction === 'add',
          name,
          amount,
          unit,
          originalString: `${amount} ${unit} ${name}`.trim(),
          nutrition: item.nutrition as Record<string, unknown> | undefined,
        };
      });
  }
  return null;
}

/** Extract the recipe object for a given recipeId from a draft API response (data.daySuggestions[].meals[].recipeSuggestions[]). */
function getRecipeFromDraftResponse(
  draftData: unknown,
  dayNumber: number,
  mealName: string | undefined,
  mealOrder: number | undefined,
  recipeId: string
): Record<string, unknown> | null {
  const d = draftData as Record<string, unknown> | null | undefined;
  const suggestions = d?.daySuggestions as Array<Record<string, unknown>> | undefined;
  if (!Array.isArray(suggestions)) return null;
  const day = suggestions.find((s) => (s.dayNumber as number) === dayNumber);
  const meals = day?.meals as Array<Record<string, unknown>> | undefined;
  if (!Array.isArray(meals)) return null;
  const meal = meals.find(
    (m) =>
      (m.mealOrder as number) === mealOrder ||
      mealKeysMatch(m.mealName as string, mealName) ||
      mealKeysMatch(m.mealType as string, mealName)
  );
  const recipes = ((meal?.recipeSuggestions as Array<Record<string, unknown>> | undefined)
    ?? (meal?.recipes as Array<Record<string, unknown>> | undefined));
  if (!Array.isArray(recipes)) return null;
  const recipe = recipes.find(
    (r) =>
      (r.id as string) === recipeId ||
      (r.recipeId as string) === recipeId ||
      (r.id as string) === String(recipeId) ||
      (r.recipeId as string) === String(recipeId)
  );
  return recipe ?? null;
}

/** Ensure each ingredient has an id for React keys */
function ensureIngredientIds(ingredients: LocalIngredient[]): LocalIngredient[] {
  return ingredients.map((ing, idx) => ({
    ...ing,
    id: ing.id || `ingredient-${Date.now()}-${idx}`,
    originalIngredientId: ing.originalIngredientId ?? ing.id ?? undefined,
  }));
}

/** Map common unit abbreviations to full names so we can match API possibleUnits (e.g. "tbs" -> "tablespoon"). */
const UNIT_ABBREV_TO_FULL: Record<string, string> = {
  tbs: 'tablespoon',
  tbsp: 'tablespoon',
  tb: 'tablespoon',
  tsp: 'teaspoon',
  t: 'teaspoon',
  c: 'cup',
  'fl oz': 'fluid ounce',
  pt: 'pint',
  qt: 'quart',
  gal: 'gallon',
  lb: 'pound',
  oz: 'oz',
  g: 'g',
  ml: 'ml',
  l: 'liter',
  serving: 'serving',
  piece: 'piece',
  pinch: 'pinch',
};

/** Normalize unit to one of the API's possibleUnits (e.g. "tbs" -> "tablespoon"). */
function normalizeUnitToPossible(unit: string, possibleUnits: string[]): string {
  if (!unit || possibleUnits.length === 0) return possibleUnits[0] || unit;
  if (possibleUnits.includes(unit)) return unit;
  const full = UNIT_ABBREV_TO_FULL[unit.toLowerCase()];
  if (full && possibleUnits.includes(full)) return full;
  return possibleUnits[0] || unit;
}

interface IngredientsTabProps {
  recipeDetails: RecipeDetails;
  draftId?: string;
  mealPlanId?: string;
  itemId?: string;
  day?: number;
  mealName?: string;
  mealOrder?: number; // Required for automated-draft updateRecipeCustomizations (PUT drafts/{id})
  recipeId?: string;
  source?: 'spoonacular' | 'edamam' | 'internal';
  onRecipeUpdate?: (updatedRecipe: any) => void;
  currentServings?: number;
  restrictIngredientEditToQuantity?: boolean;
}

const IngredientsTab: React.FC<IngredientsTabProps> = ({ 
  recipeDetails, 
  draftId, 
  mealPlanId,
  itemId,
  day, 
  mealName, 
  mealOrder, 
  recipeId, 
  source,
  onRecipeUpdate,
  currentServings = 1,
  restrictIngredientEditToQuantity = false,
}) => {
  const normalizedPropItemId = String(itemId ?? '').trim();
  const [activeItemId, setActiveItemId] = useState<string>(normalizedPropItemId);
  const effectiveItemId = activeItemId || normalizedPropItemId;
  const hasMealPlanContext = Boolean(mealPlanId);
  const finalizedContext = Boolean(mealPlanId && effectiveItemId);
  const quantityOnlyMode = restrictIngredientEditToQuantity;
  const recipeDetailsRecord = recipeDetails as unknown as Record<string, unknown>;
  const nestedRecipeRecord =
    recipeDetailsRecord.recipe && typeof recipeDetailsRecord.recipe === 'object'
      ? (recipeDetailsRecord.recipe as Record<string, unknown>)
      : undefined;
  const recipeSourceValue = String(
    recipeDetailsRecord.source
      ?? recipeDetailsRecord.creationMethod
      ?? nestedRecipeRecord?.source
      ?? nestedRecipeRecord?.creationMethod
      ?? '',
  ).toLowerCase();
  const recipeGenerationType = String(
    recipeDetailsRecord.generationType
      ?? nestedRecipeRecord?.generationType
      ?? '',
  ).toLowerCase();
  const recipeGenerationSource = String(
    recipeDetailsRecord.generationSource
      ?? nestedRecipeRecord?.generationSource
      ?? '',
  ).toLowerCase();
  const isAiGeneratedRecipe = recipeSourceValue === 'ai_generated';
  const shouldLockOriginalIngredientEditing =
    (recipeGenerationType === 'ai_assisted' && recipeGenerationSource === 'ai')
    || isAiGeneratedRecipe;
  const canEditIngredients = source?.toLowerCase() !== 'bonhappetee';
  const disableIngredientSearchInEdit = shouldLockOriginalIngredientEditing;
  const canAddIngredients = source?.toLowerCase() !== 'bonhappetee';
  const [editingId, setEditingId] = useState<string | null>(null);
  const [addingNew, setAddingNew] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIngredient, setSelectedIngredient] = useState<string>('');
  const [selectedIngredientPreviewUrl, setSelectedIngredientPreviewUrl] = useState<string>('');
  /** Id from ingredients collection (e.g. spoonacular_4053) for API replacementIngredientId */
  const [selectedIngredientId, setSelectedIngredientId] = useState<string>('');
  /**
   * Source for the *selected* ingredient result.
   * Important: when autocomplete is queried with source=all, the nutrition endpoint must still be called
   * with the selected item's source to avoid "best match" drifting to a different ingredient.
   */
  const [selectedIngredientSource, setSelectedIngredientSource] = useState<'all' | 'spoonacular' | 'edamam' | 'internal'>(() => {
    return normalizeIngredientNutritionSource(source);
  });
  /** recipeIngredientId from draft (for nutrition API in AI meal plan flow) */
  const [selectedRecipeIngredientId, setSelectedRecipeIngredientId] = useState<string>('');
  const [ingredientFeedback, setIngredientFeedback] = useState<{
    variant: 'error' | 'warning' | 'success' | 'info';
    title?: string;
    message: string;
  } | null>(null);
  const [quantity, setQuantity] = useState<string>('1');
  const [unit, setUnit] = useState<string>('');
  const [availableUnits, setAvailableUnits] = useState<string[]>([]);
  const [unitOptionLabelsByKey, setUnitOptionLabelsByKey] = useState<Record<string, string>>({});
  const [unitWeightsByKey, setUnitWeightsByKey] = useState<Record<string, number>>({});
  const gramsPerMl = resolveGramsPerMl(unitWeightsByKey);
  const [loadingUnits, setLoadingUnits] = useState(false);
  // Track original ingredient being edited (used to preserve existing unit selection)
  const [originalIngredient, setOriginalIngredient] = useState<{ name: string; unit: string } | null>(null);
  const originalIngredientRef = React.useRef<{ name: string; unit: string } | null>(null);
  // Add unique IDs to ingredients if they don't have them
  const [editedIngredients, setEditedIngredients] = useState<LocalIngredient[]>(() => {
    const ingredients = recipeDetails.ingredients || [];
    return ingredients.map((ing, idx) => ({
      ...ing,
      id: ing.id || `ingredient-${Date.now()}-${idx}`,
      originalIngredientId: (ing as unknown as { recipeIngredientId?: string; originalIngredientId?: string }).recipeIngredientId
        || (ing as unknown as { originalIngredientId?: string }).originalIngredientId
        || ing.id
        || undefined,
      recipeIngredientId: (ing as unknown as { recipeIngredientId?: string }).recipeIngredientId,
    }));
  });
  const [nutritionData, setNutritionData] = useState<any>(null);
  const [loadingNutrition, setLoadingNutrition] = useState(false);
  // Track what the nutrition data is for (to prevent showing wrong data); amountInGrams when API returns it
  const [nutritionContext, setNutritionContext] = useState<{ ingredient: string; quantity: number; unit: string; amountInGrams?: number } | null>(null);
  const [hasUserEditedSearchInEdit, setHasUserEditedSearchInEdit] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [isDeletingAny, setIsDeletingAny] = useState(false);
  const manuallyClosedRef = React.useRef(false);
  const nutritionFetchTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const selectedRecipeIngredientIdRef = React.useRef<string>('');
  const nutritionRequestIdRef = React.useRef(0);
  const NUTRITION_DEBOUNCE_MS = 400;
  const clearPendingNutritionFetch = React.useCallback(() => {
    if (nutritionFetchTimerRef.current) {
      clearTimeout(nutritionFetchTimerRef.current);
      nutritionFetchTimerRef.current = null;
    }
  }, []);
  const invalidateNutritionRequests = React.useCallback(() => {
    nutritionRequestIdRef.current += 1;
  }, []);
  const setActiveRecipeIngredientId = React.useCallback((recipeIngredientId?: string) => {
    const normalizedRecipeIngredientId = typeof recipeIngredientId === 'string' ? recipeIngredientId.trim() : '';
    selectedRecipeIngredientIdRef.current = normalizedRecipeIngredientId;
    setSelectedRecipeIngredientId(normalizedRecipeIngredientId);
  }, []);
  const setActiveOriginalIngredient = React.useCallback((ingredient: { name: string; unit: string } | null) => {
    originalIngredientRef.current = ingredient;
    setOriginalIngredient(ingredient);
  }, []);
  const applyIngredientNutritionFallback = React.useCallback((ingredient: LocalIngredient) => {
    if (!ingredient.nutrition || typeof ingredient.nutrition !== 'object') return false;
    setUnitWeightsByKey({});
    const numericQuantity = Number(ingredient.amount);
    const fallbackQuantity = Number.isFinite(numericQuantity) ? numericQuantity : 0;
    const fallbackUnit = ingredient.unit || '';
    const uiNutrition = mapIngredientNutritionToUi(ingredient.nutrition);
    setNutritionData({
      ingredient: `${fallbackQuantity} ${fallbackUnit} ${ingredient.name}`.trim(),
      amount: fallbackQuantity,
      unit: fallbackUnit,
      nutrition: uiNutrition,
    });
    setNutritionContext({
      ingredient: ingredient.name,
      quantity: fallbackQuantity,
      unit: fallbackUnit,
    });
    return true;
  }, []);
  const servingsForDisplay = (() => {
    const servings =
      (typeof recipeDetails?.servings === 'number' && Number.isFinite(recipeDetails.servings)
        ? recipeDetails.servings
        : undefined)
      ?? (typeof recipeDetails?.originalServings === 'number' && Number.isFinite(recipeDetails.originalServings)
        ? recipeDetails.originalServings
        : undefined)
      ?? (typeof currentServings === 'number' && Number.isFinite(currentServings)
        ? currentServings
        : undefined)
      ?? 1;
    return Math.round(servings * 100) / 100;
  })();

  useEffect(() => {
    setActiveItemId(String(itemId ?? '').trim());
  }, [itemId]);

  const extractItemIdFromResponse = (payload: unknown): string | undefined => {
    if (!payload || typeof payload !== 'object') return undefined;
    const record = payload as Record<string, unknown>;
    const directItemId = String(record.itemId ?? '').trim();
    if (directItemId) return directItemId;
    const nestedData = record.data;
    if (nestedData && typeof nestedData === 'object') {
      const nestedItemId = String((nestedData as Record<string, unknown>).itemId ?? '').trim();
      if (nestedItemId) return nestedItemId;
    }
    return undefined;
  };

  /**
   * After a successful customization update, reload ingredients strictly from API payload/refetch.
   * No local merge/optimistic fallback is applied.
   */
  const refreshAfterSave = React.useCallback(
    async (putResponseData: unknown): Promise<void> => {
      const itemIdFromResponse = extractItemIdFromResponse(putResponseData);
      const resolvedItemId = itemIdFromResponse || effectiveItemId || undefined;
      if (hasMealPlanContext && !resolvedItemId) {
        throw new Error('Item id is required to refresh finalized ingredient data.');
      }
      if (itemIdFromResponse && itemIdFromResponse !== activeItemId) {
        setActiveItemId(itemIdFromResponse);
      }
      // Finalized context: reload effective ingredients by item (includes overrides)
      if (finalizedContext && mealPlanId && resolvedItemId) {
        const customized = await getCustomizedRecipeByItem(mealPlanId, resolvedItemId);
        if (customized.success && customized.data) {
          const customizedData = customized.data as Record<string, unknown>;
          try {
            const ingredientsResponse = await getFinalizedMealPlanItemIngredients(mealPlanId, resolvedItemId);
            if (ingredientsResponse.success && ingredientsResponse.data) {
              const itemIngredientsData = ingredientsResponse.data as { ingredients?: Array<Record<string, unknown>> };
              const effectiveIngredients = Array.isArray(itemIngredientsData.ingredients)
                ? itemIngredientsData.ingredients.filter((ing) => ing.overrideAction !== 'remove')
                : [];
              const existingCustomization = (customizedData.customization as Record<string, unknown> | undefined) ?? {};
              customizedData.customization = {
                ...existingCustomization,
                ingredients: effectiveIngredients,
              };
            }
          } catch {
            // Ignore ingredient refresh errors and keep customized payload from API.
          }

          onRecipeUpdate?.(customizedData);
          const ingredients = getIngredientsFromResponse(customizedData);
          if (ingredients !== null) {
            setEditedIngredients(ensureIngredientIds(ingredients));
            return;
          }
        }

        // Save already succeeded; avoid showing a false error toast for a parsing mismatch.
        console.warn('Unable to parse refreshed finalized ingredients; keeping current ingredient list.');
        return;
      }

      // 1. First try to extract recipe from the PUT response (which contains the freshest draft state)
      // This avoids race conditions where getCustomizedRecipeDetails might return stale data immediately after write
      const draft = putResponseData as Record<string, unknown> | undefined;
      const recipeFromDraft =
        draft?.daySuggestions && draftId && day !== undefined && recipeId
          ? getRecipeFromDraftResponse(draft, day, mealName, mealOrder ?? undefined, String(recipeId))
          : null;

      if (recipeFromDraft) {
        onRecipeUpdate?.(recipeFromDraft);
        const ingredients = getIngredientsFromResponse(recipeFromDraft);
        if (ingredients !== null) {
          setEditedIngredients(ensureIngredientIds(ingredients));
          return;
        }
      }

      // 2. If not found in PUT response (fallback), try to refetch fresh data from server
      if (draftId && day !== undefined && mealName && recipeId) {
        const draftItemIdForRefetch = String(resolvedItemId ?? '').trim();
        if (!draftItemIdForRefetch) {
          throw new Error('Item id is required to refetch draft ingredient data.');
        }
        const refetched = await getCustomizedRecipeDetails(
          String(recipeId),
          draftId,
          day,
          mealName,
          draftItemIdForRefetch,
        );
        if (refetched.success && refetched.data) {
          onRecipeUpdate?.(refetched.data);
          const ingredients = getIngredientsFromResponse(refetched.data);
          if (ingredients !== null) {
            setEditedIngredients(ensureIngredientIds(ingredients));
            return;
          }
        }
      }

      // 3. Last chance: response might already be recipe-shaped
      if (putResponseData) {
        onRecipeUpdate?.(putResponseData);
        const ingredients = getIngredientsFromResponse(putResponseData);
        if (ingredients !== null) {
          setEditedIngredients(ensureIngredientIds(ingredients));
          return;
        }
      }

      throw new Error('Unable to load updated ingredients from API response.');
    },
    [finalizedContext, hasMealPlanContext, mealPlanId, effectiveItemId, activeItemId, draftId, day, mealName, mealOrder, recipeId, onRecipeUpdate]
  );

  useEffect(() => {
    return () => {
      clearPendingNutritionFetch();
    };
  }, [clearPendingNutritionFetch]);

  // Sync displayed ingredients when recipeDetails updates (e.g. parent passes draft recipe with customization.finalIngredients)
  useEffect(() => {
    if (editingId || addingNew) return;
    const ingredients = getIngredientsFromResponse(recipeDetails);
    if (ingredients !== null) {
      setEditedIngredients(ensureIngredientIds(ingredients));
    }
  }, [recipeDetails]);

  // Avoid firing autocomplete for prefilled/selected ingredient values in edit mode.
  const activeEditingIngredient = editingId
    ? editedIngredients.find((ingredient) => ingredient.id === editingId) ?? null
    : null;
  const canEditIngredientIdentity = !quantityOnlyMode && (
    addingNew
    || !disableIngredientSearchInEdit
    || activeEditingIngredient?.isAdded === true
  );
  const hasLockedIngredientSelection = Boolean(selectedIngredient) && searchQuery === selectedIngredient;
  const canEditUnit = !quantityOnlyMode || addingNew;
  const shouldEnableIngredientSearch = canEditIngredientIdentity &&
    searchQuery.trim().length >= 2 &&
    !hasLockedIngredientSelection &&
    (addingNew || !editingId || hasUserEditedSearchInEdit);

  // Use unified ingredient search hook
  const { formattedResults: searchResults, loading: isLoadingSearch } = useDebouncedIngredientSearch(
    searchQuery,
    {
      delay: 400,
      minLength: 2,
      searchType: 'basic',
      source: source === 'internal' ? 'internal' : source === 'spoonacular' ? 'spoonacular' : 'all',
      enabled: shouldEnableIngredientSearch,
    }
  );
  const enrichedSearchResults = useSpoonacularTagEnrichment(
    searchResults as Array<IngredientSearchResult & Record<string, unknown>>,
    { enabled: showSearchResults && !isLoadingSearch },
  ) as IngredientSearchResult[];


  // Handle manual close from SearchableDropdown
  const handleCloseDropdown = React.useCallback(() => {
    manuallyClosedRef.current = true;
    setShowSearchResults(false);
  }, []);

  // Auto-show dropdown when results arrive (but not when we have a selected ingredient)
  useEffect(() => {
    // Don't auto-show if dropdown was manually closed - check first
    if (manuallyClosedRef.current) {
      return;
    }
    
    // Don't show dropdown if we have a selected ingredient matching the query (prevents re-triggering after selection)
    if (selectedIngredient && searchQuery === selectedIngredient) {
      setShowSearchResults(false);
      return;
    }
    
    if (searchQuery.trim() && (isLoadingSearch || searchResults.length > 0)) {
      setShowSearchResults(true);
    } else if (!searchQuery.trim()) {
      setShowSearchResults(false);
    }
  }, [searchResults, searchQuery, isLoadingSearch, selectedIngredient]);

  // Reset manual close flag when user starts typing a different search (but not immediately)
  useEffect(() => {
    if (manuallyClosedRef.current && searchQuery.trim() !== '') {
      // When user types, clear the flag after a short delay to allow new searches
      const timeoutId = setTimeout(() => {
        manuallyClosedRef.current = false;
      }, 200);
      return () => clearTimeout(timeoutId);
    }
  }, [searchQuery]);
  const handleEditIngredient = async (id: string) => {
    const ingredient = editedIngredients.find(ing => ing.id === id);
    if (!ingredient) {
      console.error('Ingredient not found with id:', id);
      return;
    }
    clearPendingNutritionFetch();
    invalidateNutritionRequests();
    setHasUserEditedSearchInEdit(false);
    setShowSearchResults(false);
    setEditingId(id);
    // Only set searchQuery when not quantity-only (avoids triggering basic autocomplete API)
    const canEditThisIngredientIdentity = !quantityOnlyMode && (
      !disableIngredientSearchInEdit
      || ingredient.isAdded === true
    );
    if (canEditThisIngredientIdentity) {
      setSearchQuery(ingredient.name);
    }
    setAddingNew(false);
    setSelectedIngredient(ingredient.name);
    setSelectedIngredientPreviewUrl((ingredient as { image?: string })?.image || '');
    const catalogIngredientId = String(ingredient.ingredientId ?? '').trim();
    setSelectedIngredientId(catalogIngredientId);
    const ingredientNutritionSource = normalizeIngredientNutritionSource(
      ingredient.source ?? ingredient.provider,
      normalizeIngredientNutritionSource(source)
    );
    setSelectedIngredientSource(ingredientNutritionSource);
    // Only store true recipe ingredient IDs for /ingredients/nutrition?recipeIngredientId=
    setActiveRecipeIngredientId(ingredient.recipeIngredientId || '');
    setQuantity(ingredient.amount?.toString() || '1');
    setUnitWeightsByKey({});
    setNutritionData(null); // Clear old nutrition data immediately
    setNutritionContext(null); // Clear old nutrition context
    // Store original ingredient so we can keep its unit selected when possible
    setActiveOriginalIngredient({ name: ingredient.name, unit: ingredient.unit || '' });

    const existingUnit = ingredient.unit || '';
    const hasRecipeIngredientId = Boolean(
      ingredient.recipeIngredientId && /^[a-fA-F0-9]{24}$/.test(ingredient.recipeIngredientId)
    );

    if (quantityOnlyMode) {
      // Quantity-only mode: unit is read-only, skip autocomplete (units_only) API call
      setAvailableUnits([]);
      setUnitWeightsByKey({});
      setUnit(existingUnit);
      if (existingUnit && ingredient.amount !== undefined && ingredient.amount !== null) {
        await fetchNutritionData(
          ingredient.name,
          ingredient.amount,
          existingUnit,
          ingredient.recipeIngredientId,
          ingredientNutritionSource,
          catalogIngredientId || undefined
        );
      } else if (hasRecipeIngredientId && ingredient.amount !== undefined && ingredient.amount !== null) {
        // Some Spoonacular ingredients persist with sentinel/empty serving units.
        // Fetch by recipeIngredientId without forcing unit so API can return valid possibleUnits.
        await fetchNutritionData(
          ingredient.name,
          ingredient.amount,
          '',
          ingredient.recipeIngredientId,
          ingredientNutritionSource,
          catalogIngredientId || undefined
        );
      } else {
        applyIngredientNutritionFallback(ingredient);
      }
      return;
    }

    // Do not trigger units_only autocomplete while entering edit mode.
    // Keep the current unit and lazily fetch units only when ingredient selection changes.
    setLoadingUnits(false);
    setAvailableUnits(existingUnit ? [existingUnit] : []);
    setUnitWeightsByKey({});
    setUnit(existingUnit);
    if (existingUnit && ingredient.amount !== undefined && ingredient.amount !== null) {
      await fetchNutritionData(
        ingredient.name,
        ingredient.amount,
        existingUnit,
        ingredient.recipeIngredientId,
        ingredientNutritionSource,
        catalogIngredientId || undefined
      );
    } else if (hasRecipeIngredientId && ingredient.amount !== undefined && ingredient.amount !== null) {
      // Trigger initial nutrition + unit metadata lookup even when unit is missing.
      await fetchNutritionData(
        ingredient.name,
        ingredient.amount,
        '',
        ingredient.recipeIngredientId,
        ingredientNutritionSource,
        catalogIngredientId || undefined
      );
    } else {
      applyIngredientNutritionFallback(ingredient);
    }
  };

  const handleAddNew = () => {
    // Reset all states when adding new
    clearPendingNutritionFetch();
    invalidateNutritionRequests();
    setHasUserEditedSearchInEdit(false);
    setEditingId(null);
    setAddingNew(true);
    setSearchQuery('');
    setSelectedIngredient('');
    setSelectedIngredientPreviewUrl('');
    setSelectedIngredientId('');
    setSelectedIngredientSource(normalizeIngredientNutritionSource(source));
    setActiveRecipeIngredientId('');
    setQuantity('1');
    setUnit('');
    setAvailableUnits([]);
    setUnitWeightsByKey({});
    setLoadingUnits(false);
    setShowSearchResults(false);
    setNutritionData(null); // Clear old nutrition data
    setNutritionContext(null); // Clear old nutrition context
    setActiveOriginalIngredient(null); // Clear original ingredient
  };

  const handleSelectIngredient = async (
    item: {
      id?: string;
      name: string;
      possibleUnits?: string[];
      source?: string;
      provider?: string;
      image?: string;
      imageUrl?: string;
    } | string
  ) => {
    if (!canEditIngredientIdentity && editingId !== null) {
      return;
    }
    clearPendingNutritionFetch();
    invalidateNutritionRequests();
    const ingredientName = typeof item === 'string' ? item : item.name;
    const ingredientPreviewUrl = typeof item === 'string'
      ? ''
      : String(item.imageUrl ?? item.image ?? '').trim();
    const rawIngredientId = typeof item === 'string' ? '' : (item.id ?? '');
    const ingredientId = rawIngredientId.startsWith('ingredient-') ? '' : rawIngredientId;
    const ingredientSourceRaw = typeof item === 'string' ? undefined : item.source;
    const ingredientProviderRaw = typeof item === 'string' ? undefined : item.provider;
    const resolvedIngredientSource = normalizeIngredientNutritionSource(
      ingredientSourceRaw ?? ingredientProviderRaw,
      normalizeIngredientNutritionSource(source)
    );
    setSelectedIngredientId(ingredientId);
    setSelectedIngredientSource(resolvedIngredientSource);

    setSelectedIngredient(ingredientName);
    setSelectedIngredientPreviewUrl(ingredientPreviewUrl);
    setSearchQuery(ingredientName);
    setShowSearchResults(false);
    setUnitOptionLabelsByKey({});
    setUnitWeightsByKey({});
    setLoadingUnits(true);
    setNutritionData(null);
    setNutritionContext(null);

    try {
      const shouldPassSelectedUnit = originalIngredient && originalIngredient.name === ingredientName;
      const selectedResultUnits =
        typeof item === 'string'
          ? []
          : normalizeUnits(Array.isArray(item.possibleUnits) ? item.possibleUnits : []);
      const units = selectedResultUnits.length > 0 ? selectedResultUnits : ['g'];
      setAvailableUnits(units);
      setUnitOptionLabelsByKey({});
      setUnitWeightsByKey({});
      const selectedUnit = shouldPassSelectedUnit
        ? normalizeUnitToPossible(originalIngredient.unit, units)
        : units[0] || '';
      setUnit(selectedUnit);
      if (selectedUnit && quantity !== undefined && quantity !== null && quantity !== '') {
        const numQuantity = parseFloat(quantity);
        if (!isNaN(numQuantity) && numQuantity >= 0) {
          await fetchNutritionData(
            ingredientName,
            numQuantity,
            selectedUnit,
            selectedRecipeIngredientIdRef.current || undefined,
            resolvedIngredientSource,
            ingredientId || undefined
          );
        }
      }
    } catch (error) {
      console.error('Error fetching units:', error);
      
      setIngredientFeedback({
        variant: 'error',
        title: 'Failed to load units',
        message: 'Failed to load units for this ingredient. Please try again.',
      });
      
      // Clear the selection on error
      setSelectedIngredient('');
      setSelectedIngredientPreviewUrl('');
      setSelectedIngredientId('');
      setSearchQuery('');
    } finally {
      setLoadingUnits(false);
    }
  };

  const handleUnitChange = async (newUnit: string) => {
    if (!canEditUnit) {
      return;
    }
    clearPendingNutritionFetch();
    invalidateNutritionRequests();
    setUnit(newUnit);
    setNutritionData(null);
    setNutritionContext(null);
    
    // Fetch nutrition when unit changes and we have ingredient and quantity (even if 0)
    if (selectedIngredient && quantity !== undefined && quantity !== null && quantity !== '' && newUnit) {
      const numQuantity = parseFloat(quantity);
      if (!isNaN(numQuantity) && numQuantity >= 0) {
        await fetchNutritionData(
          selectedIngredient,
          numQuantity,
          newUnit,
          selectedRecipeIngredientIdRef.current || undefined,
          undefined,
          selectedIngredientId || undefined
        );
      }
    }
  };

  const handleQuantityChange = (newQuantity: string) => {
    setQuantity(newQuantity);

    clearPendingNutritionFetch();
    invalidateNutritionRequests();
    setNutritionData(null);
    setNutritionContext(null);

    if (!selectedIngredient || !unit || newQuantity === undefined || newQuantity === null || newQuantity === '') {
      return;
    }
    const numQuantity = parseFloat(newQuantity);
    if (isNaN(numQuantity) || numQuantity < 0) return;

    nutritionFetchTimerRef.current = setTimeout(() => {
      nutritionFetchTimerRef.current = null;
      void fetchNutritionData(
        selectedIngredient,
        numQuantity,
        unit,
        selectedRecipeIngredientIdRef.current || undefined,
        undefined,
        selectedIngredientId || undefined
      );
    }, NUTRITION_DEBOUNCE_MS);
  };

  const fetchNutritionData = async (
    ingredient: string,
    amount: number,
    unitParam: string,
    recipeIngredientIdParam?: string,
    ingredientSourceParam?: 'all' | 'spoonacular' | 'edamam' | 'internal',
    ingredientIdParam?: string
  ) => {
    const requestId = nutritionRequestIdRef.current + 1;
    nutritionRequestIdRef.current = requestId;
    try {
      const rid = recipeIngredientIdParam ?? selectedRecipeIngredientIdRef.current ?? selectedRecipeIngredientId;
      const useRecipeIngredientId = rid && /^[a-fA-F0-9]{24}$/.test(rid);
      const normalizedIngredient = ingredient.trim().toLowerCase();
      const originalIngredientSnapshot = originalIngredientRef.current ?? originalIngredient;
      const normalizedOriginalIngredient = originalIngredientSnapshot?.name?.trim().toLowerCase() ?? '';
      const normalizedUnit = unitParam.trim().toLowerCase();
      const ingredientChanged = Boolean(normalizedOriginalIngredient && normalizedOriginalIngredient !== normalizedIngredient);
      const shouldUseRecipeIngredientLookup = Boolean(useRecipeIngredientId && !ingredientChanged);
      const ingredientLookupId = String(ingredientIdParam ?? selectedIngredientId ?? '').trim();
      const sourceForNutrition = ingredientSourceParam ?? selectedIngredientSource;
      setLoadingNutrition(true);
      setNutritionData(null);
      setNutritionContext(null);
      const response = shouldUseRecipeIngredientLookup
        ? await getIngredientNutritionByRecipeIngredientId(
            rid,
            amount,
            normalizedUnit ? unitParam : undefined
          )
        : ingredientLookupId
          ? await getIngredientNutritionByIngredientId(
              ingredientLookupId,
              amount,
              unitParam
            )
        : await getIngredientNutrition(
            ingredient,
            sourceForNutrition,
            amount,
            unitParam
          );
      if (requestId !== nutritionRequestIdRef.current) {
        return;
      }

      if (response.success && response.nutrition) {
        const metadata = extractIngredientUnitMetadata(response);
        const unitsFromResponse = normalizeUnits(metadata.units);
        setUnitOptionLabelsByKey(metadata.labelsByKey);
        setUnitWeightsByKey(metadata.weightsByKey);
        if (unitsFromResponse.length > 0) {
          setAvailableUnits(unitsFromResponse);
          if (!unitsFromResponse.includes(unitParam)) {
            const normalizedSelected = normalizeUnitToPossible(unitParam, unitsFromResponse);
            setUnit(normalizedSelected);
          }
        }
        const mappedData = {
          ingredient: response.ingredient || `${amount} ${unitParam} ${ingredient}`,
          amount: amount,
          unit: unitParam,
          nutrition: {
            calories: response.nutrition.calories || 0,
            protein: response.nutrition.macros?.protein || 0,
            carbs: response.nutrition.macros?.carbs || 0,
            fat: response.nutrition.macros?.fat || 0,
            fiber: response.nutrition.macros?.fiber || 0,
            sugar: response.nutrition.macros?.sugar || 0,
            sodium: response.nutrition.macros?.sodium || 0,
            cholesterol: response.nutrition.macros?.cholesterol || 0,
            saturatedFat: response.nutrition.macros?.saturatedFat || 0,
            transFat: response.nutrition.macros?.transFat || 0,
            monounsaturatedFat: response.nutrition.macros?.monounsaturatedFat || 0,
            polyunsaturatedFat: response.nutrition.macros?.polyunsaturatedFat || 0,
            // Micronutrients
            vitaminA: response.nutrition.micros?.vitamins?.vitaminA || 0,
            vitaminC: response.nutrition.micros?.vitamins?.vitaminC || 0,
            vitaminD: response.nutrition.micros?.vitamins?.vitaminD || 0,
            vitaminE: response.nutrition.micros?.vitamins?.vitaminE || 0,
            vitaminK: response.nutrition.micros?.vitamins?.vitaminK || 0,
            thiamin: response.nutrition.micros?.vitamins?.thiamin || 0,
            riboflavin: response.nutrition.micros?.vitamins?.riboflavin || 0,
            niacin: response.nutrition.micros?.vitamins?.niacin || 0,
            vitaminB6: response.nutrition.micros?.vitamins?.vitaminB6 || 0,
            folate: response.nutrition.micros?.vitamins?.folate || 0,
            vitaminB12: response.nutrition.micros?.vitamins?.vitaminB12 || 0,
            biotin: response.nutrition.micros?.vitamins?.biotin || 0,
            pantothenicAcid: response.nutrition.micros?.vitamins?.pantothenicAcid || 0,
            calcium: response.nutrition.micros?.minerals?.calcium || 0,
            iron: response.nutrition.micros?.minerals?.iron || 0,
            magnesium: response.nutrition.micros?.minerals?.magnesium || 0,
            phosphorus: response.nutrition.micros?.minerals?.phosphorus || 0,
            potassium: response.nutrition.micros?.minerals?.potassium || 0,
            zinc: response.nutrition.micros?.minerals?.zinc || 0,
            copper: response.nutrition.micros?.minerals?.copper || 0,
            manganese: response.nutrition.micros?.minerals?.manganese || 0,
            selenium: response.nutrition.micros?.minerals?.selenium || 0,
            iodine: response.nutrition.micros?.minerals?.iodine || 0,
            chromium: response.nutrition.micros?.minerals?.chromium || 0,
            molybdenum: response.nutrition.micros?.minerals?.molybdenum || 0,
          }
        };
        
        // Always set the nutrition data - validation happens at display time
        setNutritionData(mappedData);
        const amountInGrams = (response as { amountInGrams?: number }).amountInGrams;
        setNutritionContext({ ingredient, quantity: amount, unit: unitParam, ...(amountInGrams != null && { amountInGrams }) });
      } else {
        setNutritionData(null);
        setNutritionContext(null);
      }
    } catch (error) {
      if (requestId !== nutritionRequestIdRef.current) {
        return;
      }
      console.error('Error fetching nutrition data:', error);
      setNutritionData(null);
      setNutritionContext(null);
      if (!quantityOnlyMode) {
        setIngredientFeedback({
          variant: 'warning',
          title: 'Nutrition fetch issue',
          message: 'Failed to fetch nutrition data. You can still save the ingredient.',
        });
      }
    } finally {
      if (requestId === nutritionRequestIdRef.current) {
        setLoadingNutrition(false);
      }
    }
  };

  const handleSaveIngredient = async () => {
    const originalEditingIngredient =
      editingId !== null ? editedIngredients.find(ing => ing.id === editingId) : null;

    if (editingId !== null && !originalEditingIngredient) {
      console.error('Original ingredient not found');
      return;
    }

    const effectiveIngredient = quantityOnlyMode && originalEditingIngredient
      ? originalEditingIngredient.name
      : selectedIngredient;
    const effectiveUnit = quantityOnlyMode && originalEditingIngredient
      ? (originalEditingIngredient.unit || unit)
      : unit;

    if (!effectiveIngredient || quantity === undefined || quantity === null || quantity === '' || !effectiveUnit) {
      setIngredientFeedback({
        variant: 'warning',
        title: 'Missing information',
        message: 'Please fill in all fields.',
      });
      return;
    }

    const numQuantity = parseFloat(quantity);
    if (isNaN(numQuantity) || numQuantity < 0) {
      setIngredientFeedback({
        variant: 'warning',
        title: 'Invalid quantity',
        message: 'Please enter a valid quantity (0 or greater).',
      });
      return;
    }

    let modification: IngredientModification;
    
    if (editingId !== null) {
      const originalIngredient = editedIngredients.find(ing => ing.id === editingId);
      if (!originalIngredient) {
        console.error('Original ingredient not found');
        return;
      }
      const isSameIngredient = quantityOnlyMode
        ? true
        : effectiveIngredient.trim().toLowerCase() === originalIngredient.name.trim().toLowerCase();
      const newAmountGrams = nutritionContext?.ingredient === effectiveIngredient &&
        nutritionContext?.quantity === numQuantity &&
        nutritionContext?.unit === effectiveUnit &&
        nutritionContext?.amountInGrams != null
        ? nutritionContext.amountInGrams
        : undefined;
      const baseMod = {
        originalIngredient: originalIngredient.name,
        originalIngredientName: originalIngredient.name,
        originalAmount: originalIngredient.amount,
        originalUnit: originalIngredient.unit || '',
        originalIngredientId: originalIngredient.originalIngredientId || originalIngredient.id,
      };
      if (isSameIngredient) {
        modification = {
          ...baseMod,
          type: 'modify',
          amount: numQuantity,
          unit: effectiveUnit,
          ...(newAmountGrams != null && { newAmountGrams }),
        };
      } else {
        modification = {
          ...baseMod,
          type: 'replace',
          newIngredient: effectiveIngredient,
          amount: numQuantity,
          unit: effectiveUnit,
          replacementIngredientId: selectedIngredientId || undefined,
          ...(newAmountGrams != null && { newAmountGrams }),
        };
      }
    } else {
      // Adding new ingredient - this is an add modification
      modification = {
        type: 'add',
        newIngredient: effectiveIngredient,
        amount: numQuantity,
        unit: effectiveUnit,
        replacementIngredientId: selectedIngredientId || undefined,
      };
    }
    
    const toItemOverride = (value: IngredientModification) => {
      const action: 'remove' | 'replace' | 'modify' | 'add' =
        value.type === 'omit' ? 'remove' : value.type;
      return {
        action,
        ...(value.originalIngredientId && { originalIngredientId: value.originalIngredientId }),
        ...(value.originalAmount != null && { originalAmount: value.originalAmount }),
        ...(value.replacementIngredientId && { replacementIngredientId: value.replacementIngredientId }),
        ...(value.newIngredient && { replacementName: value.newIngredient }),
        ...(value.amount != null && { newAmount: value.amount }),
        ...(value.unit && { servingId: value.unit }),
        ...(value.newAmountGrams != null && { newAmountGrams: value.newAmountGrams }),
      };
    };

    if (hasMealPlanContext && !effectiveItemId) {
      setIngredientFeedback({
        variant: 'warning',
        title: 'Item id missing',
        message: 'Unable to save ingredient because item id is required.',
      });
      return;
    }

    // Finalized plan: persist ingredient customization on meal plan item
    if (finalizedContext && mealPlanId && effectiveItemId) {
      const finalizedMealPlanId = mealPlanId;
      const finalizedItemId = effectiveItemId;
      setIsSaving(true);
      try {
        const response = await customizeFinalizedMealPlanItem(finalizedMealPlanId, {
          itemId: finalizedItemId,
          overrides: [toItemOverride(modification)],
        });

        if (response.success) {
          await refreshAfterSave(response.data);
          setIngredientFeedback({
            variant: 'success',
            title: 'Ingredient saved',
            message: editingId !== null ? 'Ingredient updated successfully.' : 'Ingredient added successfully.',
          });
        } else {
          throw new Error(response.message || 'Failed to save customization');
        }
      } catch (error) {
        console.error('Error saving finalized ingredient customization:', error);
        setIngredientFeedback({
          variant: 'error',
          title: 'Unable to save ingredient',
          message: extractErrorMessage(error),
        });
      } finally {
        setIsSaving(false);
      }
    // Call API for draft contexts if all required context is available
    } else if (!hasMealPlanContext && draftId && day !== undefined && mealName && recipeId) {
      if (!mealOrder || mealOrder < 1) {
        setIngredientFeedback({
          variant: 'warning',
          title: 'Meal order missing',
          message: 'Unable to save ingredient changes because meal order is missing.',
        });
        return;
      }
      if (!source) {
        setIngredientFeedback({
          variant: 'warning',
          title: 'Recipe source missing',
          message: 'Unable to save ingredient changes because recipe source is missing.',
        });
        return;
      }
      setIsSaving(true);
      try {
        const response = await updateRecipeCustomizations(
          draftId,
          day,
          mealOrder,
          recipeId,
          {
            recipeId,
            source,
            modifications: [modification],
          },
        );

        if (response.success) {
          await refreshAfterSave(response.data);
          setIngredientFeedback({
            variant: 'success',
            title: 'Ingredient saved',
            message: editingId !== null ? 'Ingredient updated successfully.' : 'Ingredient added successfully.',
          });
        } else {
          throw new Error(response.message || 'Failed to save customization');
        }
      } catch (error) {
        console.error('Error saving ingredient customization:', error);
        setIngredientFeedback({
          variant: 'error',
          title: 'Unable to save ingredient',
          message: extractErrorMessage(error),
        });
      } finally {
        setIsSaving(false);
      }
    } else {
      setIngredientFeedback({
        variant: 'warning',
        title: 'Missing context',
        message: 'Unable to save ingredient because required context is missing.',
      });
      return;
    }
    
    handleCancel();
  };

  const handleRemoveIngredient = async (id: string) => {
    // Prevent multiple deletions
    if (isDeletingAny) {
      return;
    }
    
    const ingredientToRemove = editedIngredients.find(ing => ing.id === id);
    if (!ingredientToRemove) {
      console.error('Ingredient not found with id:', id);
      return;
    }

    const originalIngredientId = ingredientToRemove.originalIngredientId || ingredientToRemove.id;

    if (hasMealPlanContext && !effectiveItemId) {
      setIngredientFeedback({
        variant: 'warning',
        title: 'Item id missing',
        message: 'Unable to remove ingredient because item id is required.',
      });
      return;
    }
    
    // Finalized plan: remove ingredient override on item
    if (finalizedContext && mealPlanId && effectiveItemId && originalIngredientId) {
      const finalizedMealPlanId = mealPlanId;
      const finalizedItemId = effectiveItemId;
      setDeletingId(id);
      setIsDeletingAny(true);
      try {
        const response = await customizeFinalizedMealPlanItem(finalizedMealPlanId, {
          itemId: finalizedItemId,
          overrides: [
            {
              action: 'remove',
              originalIngredientId,
            },
          ],
        });

        if (response.success) {
          await refreshAfterSave(response.data);
          setIngredientFeedback({
            variant: 'success',
            title: 'Ingredient removed',
            message: 'Ingredient removed successfully.',
          });
        } else {
          throw new Error(response.message || 'Failed to remove ingredient');
        }
      } catch (error) {
        console.error('Error removing finalized ingredient:', error);
        setIngredientFeedback({
          variant: 'error',
          title: 'Unable to remove ingredient',
          message: extractErrorMessage(error),
        });
      } finally {
        setDeletingId(null);
        setIsDeletingAny(false);
      }
    // Call API for draft context if all required context is available
    } else if (!hasMealPlanContext && draftId && day !== undefined && recipeId && mealOrder && mealOrder > 0 && originalIngredientId) {
      setDeletingId(id);
      setIsDeletingAny(true);
      try {
        const response = await removeDraftRecipeIngredient(
          draftId,
          day,
          mealOrder,
          recipeId,
          originalIngredientId
        );

        if (response.success) {
          await refreshAfterSave(response.data);
          setIngredientFeedback({
            variant: 'success',
            title: 'Ingredient removed',
            message: 'Ingredient removed successfully.',
          });
        } else {
          throw new Error(response.message || 'Failed to remove ingredient');
        }
      } catch (error) {
        console.error('Error removing ingredient:', error);
        setIngredientFeedback({
          variant: 'error',
          title: 'Unable to remove ingredient',
          message: extractErrorMessage(error),
        });
      } finally {
        setDeletingId(null);
        setIsDeletingAny(false);
      }
    } else {
      setIngredientFeedback({
        variant: 'warning',
        title: 'Missing context',
        message: 'Unable to remove ingredient because required context is missing.',
      });
    }
  };

  const handleCancel = () => {
    clearPendingNutritionFetch();
    invalidateNutritionRequests();
    setHasUserEditedSearchInEdit(false);
    setEditingId(null);
    setAddingNew(false);
    setSearchQuery('');
    setSelectedIngredient('');
    setSelectedIngredientPreviewUrl('');
    setQuantity('1');
    setUnit('');
    setSelectedIngredientId('');
    setActiveRecipeIngredientId('');
    setAvailableUnits([]);
    setUnitWeightsByKey({});
    setShowSearchResults(false);
    setLoadingUnits(false);
    setNutritionData(null); // Clear nutrition data on cancel
    setNutritionContext(null); // Clear nutrition context on cancel
    setActiveOriginalIngredient(null); // Clear original ingredient
  };

  const listItems: RecipeIngredientListItem[] = editedIngredients.map((ingredient) => {
    const canEditThisIngredient = canEditIngredients && (
      !shouldLockOriginalIngredientEditing || ingredient.isAdded === true
    );
    const nutrition = mapIngredientNutritionToUi(
      ingredient.nutrition && typeof ingredient.nutrition === 'object'
        ? ingredient.nutrition as Record<string, unknown>
        : {}
    );
    return {
      id: ingredient.id,
      ingredientId: ingredient.originalIngredientId,
      canEdit: canEditThisIngredient,
      name: ingredient.name,
      amount: Number(ingredient.amount) || 0,
      unit: ingredient.unit || '',
      nutritionData: {
        calories: Number(nutrition.calories) || 0,
        macros: {
          protein: Number(nutrition.protein) || 0,
          carbs: Number(nutrition.carbs) || 0,
          fat: Number(nutrition.fat) || 0,
          fiber: Number(nutrition.fiber) || 0,
          saturatedFat: Number(nutrition.saturatedFat) || 0,
        },
      },
    };
  });
  const numericQuantity = Number.parseFloat(quantity);
  const hasValidQuantity = Number.isFinite(numericQuantity) && numericQuantity >= 0;
  const canSubmitIngredient = Boolean(
    selectedIngredient.trim() && unit.trim() && hasValidQuantity && !isSaving && !loadingNutrition
  );
  const recipeServingDetailsRecord = recipeDetails as RecipeDetails & {
    customization?: Record<string, unknown>;
    recipe?: Record<string, unknown>;
  };
  const customizationRecord = asRecord(recipeServingDetailsRecord.customization);
  const recipeRecord = asRecord(recipeServingDetailsRecord.recipe);
  const servingDisplayDefaultId =
    typeof recipeDetails.defaultServingId === 'string' && recipeDetails.defaultServingId.trim().length > 0
      ? recipeDetails.defaultServingId
      : typeof customizationRecord?.defaultServingId === 'string' && customizationRecord.defaultServingId.trim().length > 0
        ? customizationRecord.defaultServingId.trim()
        : typeof recipeRecord?.defaultServingId === 'string' && recipeRecord.defaultServingId.trim().length > 0
          ? recipeRecord.defaultServingId.trim()
          : null;
  const servingDisplaySelectedId =
    typeof recipeDetails.selectedServingId === 'string' && recipeDetails.selectedServingId.trim().length > 0
      ? recipeDetails.selectedServingId
      : typeof customizationRecord?.selectedServingId === 'string' && customizationRecord.selectedServingId.trim().length > 0
        ? customizationRecord.selectedServingId.trim()
        : servingDisplayDefaultId;
  const servingDisplayDefaultGrams =
    Number.isFinite(Number(recipeDetails.defaultServingGrams)) && Number(recipeDetails.defaultServingGrams) > 0
      ? Number(recipeDetails.defaultServingGrams)
      : Number.isFinite(Number(customizationRecord?.defaultServingGrams)) && Number(customizationRecord?.defaultServingGrams) > 0
        ? Number(customizationRecord?.defaultServingGrams)
        : Number.isFinite(Number(recipeRecord?.defaultServingGrams)) && Number(recipeRecord?.defaultServingGrams) > 0
          ? Number(recipeRecord?.defaultServingGrams)
          : null;
  const servingDisplaySelectedGrams =
    Number.isFinite(Number(recipeDetails.selectedServingGrams)) && Number(recipeDetails.selectedServingGrams) > 0
      ? Number(recipeDetails.selectedServingGrams)
      : Number.isFinite(Number(customizationRecord?.selectedServingGrams)) && Number(customizationRecord?.selectedServingGrams) > 0
        ? Number(customizationRecord?.selectedServingGrams)
        : servingDisplayDefaultGrams;
  const servingDisplaySizes = (
    Array.isArray(recipeDetails.servingSizes) && recipeDetails.servingSizes.length > 0
      ? recipeDetails.servingSizes
      : Array.isArray(customizationRecord?.servingSizes) && customizationRecord.servingSizes.length > 0
        ? customizationRecord.servingSizes
        : Array.isArray(recipeRecord?.servingSizes) && recipeRecord.servingSizes.length > 0
          ? recipeRecord.servingSizes
          : undefined
  ) as RecipeServingSize[] | undefined;
  const servingDisplayValue = getRecipeServingDisplayValue({
    defaultServingId: servingDisplayDefaultId,
    defaultServingGrams: servingDisplayDefaultGrams,
    selectedServingId: servingDisplaySelectedId,
    selectedServingGrams: servingDisplaySelectedGrams,
    servingSizes: servingDisplaySizes,
  });

  const renderIngredientItem = (ingredient: any, index: number) => {
    if (editingId === ingredient.id) {
      return (
        <div
          key={ingredient.id || index}
          className="flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-[0px_8px_20px_-4px_rgba(0,0,0,0.1),0px_4px_8px_-2px_rgba(0,0,0,0.06)]"
          style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
        >
          <div className="space-y-4">
            {/* Ingredient Search */}
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                {canEditIngredientIdentity ? 'Search Ingredient' : 'Ingredient'}
              </label>
              {!canEditIngredientIdentity ? (
                <Input
                  value={selectedIngredient}
                  disabled
                  readOnly
                  className="h-10 border-slate-200 bg-slate-50 text-slate-700"
                />
              ) : (
                <>
                  <label className="flex h-[38px] cursor-text items-center gap-2 rounded-[14px] border border-[#e5e7eb] bg-white px-[13px] transition-colors focus-within:border-[#d1d5dc]">
                    <Search className="pointer-events-none size-[14px] shrink-0 text-[#99a1af]" strokeWidth={2} aria-hidden />
                    <input
                      type="text"
                      autoComplete="off"
                      enterKeyHint="search"
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setSelectedIngredient('');
                        setSelectedIngredientId('');
                        setHasUserEditedSearchInEdit(true);
                      }}
                      placeholder="Search by Ingredients"
                      aria-label="Search by ingredients"
                      className="min-h-0 min-w-0 flex-1 border-0 bg-transparent p-0 text-[13px] tracking-[-0.0762px] text-[#1e2939] placeholder:text-[#99a1af] shadow-none outline-none ring-0 focus:border-0 focus:shadow-none focus:outline-none focus:ring-0 focus-visible:border-0 focus-visible:outline-none focus-visible:ring-0"
                    />
                    {isLoadingSearch ? (
                      <Loader2 className="size-4 shrink-0 animate-spin text-[#20aedc]" aria-hidden />
                    ) : null}
                  </label>
                  {!selectedIngredient.trim() && searchQuery.trim().length >= 2 && !isLoadingSearch ? (
                    <p className="text-xs leading-[18px] text-[#9ca3af]">
                      {enrichedSearchResults.length === 0
                        ? 'No ingredients found'
                        : `${enrichedSearchResults.length} Ingredients found`}
                    </p>
                  ) : null}
                  {showSearchResults && !selectedIngredient.trim() ? (
                    <section className="max-h-48 overflow-y-auto">
                      {enrichedSearchResults.map((item) => {
                        const rowId = String(item.id ?? item.name);
                        const thumb =
                          typeof item.imageUrl === 'string'
                            ? item.imageUrl
                            : typeof item.image === 'string'
                              ? item.image
                              : undefined;
                        const description = typeof item.description === 'string' ? item.description.trim() : '';
                        return (
                          <div key={rowId} className="border-b border-[#f3f4f6] py-1 last:border-0">
                            <button
                              type="button"
                              onClick={() => void handleSelectIngredient(item)}
                              className="flex w-full items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-slate-50"
                              aria-label={`Select ${item.name}`}
                            >
                              <AddIngredientResultThumbnail imageUrl={thumb} name={item.name} />
                              <div className="min-w-0 flex-1">
                                <p className="truncate text-[13px] font-semibold leading-4 tracking-[-0.0762px] text-[#1e2939]">
                                  {item.name}
                                </p>
                                {item.aisle ? (
                                  <p className="mt-0.5 truncate text-xs text-slate-500">{item.aisle}</p>
                                ) : null}
                                {description ? (
                                  <p className="mt-0.5 line-clamp-2 text-xs leading-4 text-slate-500">
                                    {description}
                                  </p>
                                ) : null}
                                <RecipeAllergenCard
                                  allergens={item.allergens}
                                  compact
                                  maxVisible={3}
                                  className="mt-1"
                                />
                                <SpoonacularTagSummary item={item} className="mt-1" />
                              </div>
                              <div className="flex h-7 items-center justify-center gap-1.5 rounded-full bg-[#eefbff] px-3 text-xs font-semibold text-[#0e7490] transition-colors">
                                Select
                              </div>
                            </button>
                          </div>
                        );
                      })}
                    </section>
                  ) : null}
                </>
              )}
            </div>

            {/* Quantity and Unit */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Quantity</label>
                <Input
                  type="number"
                  value={quantity}
                  onChange={(e) => handleQuantityChange(e.target.value)}
                  onWheel={(e) => e.currentTarget.blur()}
                  placeholder="1"
                  min="0"
                  step="0.1"
                  className="h-10 border-slate-200 bg-white focus:border-slate-300 focus:ring-slate-300 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
              </div>
              <div className="space-y-2">
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Unit</label>
                {!canEditUnit ? (
                  <Input
                    value={unit || (loadingUnits ? 'Loading...' : 'No unit available')}
                    disabled
                    readOnly
                    className="h-10 border-slate-200 bg-slate-50 text-slate-700"
                  />
                ) : (
                  <Select value={unit} onValueChange={handleUnitChange} disabled={loadingUnits}>
                    <SelectTrigger className="h-10 border-slate-200 bg-white focus:border-slate-300 focus:ring-slate-300">
                      <SelectValue placeholder={loadingUnits ? "Loading..." : "Select unit"} />
                    </SelectTrigger>
                    <SelectContent className="z-[9999] w-[var(--radix-select-trigger-width)]" position="popper" side="bottom" align="start" sideOffset={4}>
                          {availableUnits.length > 0 ? (
                            availableUnits.map((unitOption) => (
                              <SelectItem key={unitOption} value={unitOption} className="text-xs py-1.5">
                            {unitOptionLabelsByKey[normalizeUnitKey(unitOption)] ?? formatGramMlWeightLabel(
                              unitOption,
                              unitWeightsByKey[normalizeUnitKey(unitOption)],
                              gramsPerMl,
                            )}
                              </SelectItem>
                            ))
                          ) : (
                        <SelectItem value="no-units" disabled className="text-xs py-1.5">
                          No units available
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>

            {quantityOnlyMode && (
              <div className="flex items-start gap-2 p-3 border border-amber-200 rounded-lg bg-amber-50">
                <Info className="h-4 w-4 mt-0.5 text-amber-700" />
                <p className="text-sm text-amber-800">
                  Changing unit of existing ingredients is not allowed in AI meal plan. Please add or delete ingredient.
                </p>
              </div>
            )}

            {/* Nutrition Data Display - Inside Edit Form */}
            {loadingNutrition && (
              <div className="rounded-[14px] border border-[#e0f2fe] bg-[#f0f9ff] p-3">
                <div className="flex items-center justify-center gap-3 text-[#0369a1]">
                  <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-[#0369a1]"></div>
                  <span className="text-sm font-medium">Fetching nutrition...</span>
                </div>
              </div>
            )}
            
            {!loadingNutrition && nutritionData && nutritionData.nutrition && 
             nutritionContext?.ingredient === selectedIngredient && (
              <div className="rounded-[14px] border border-[#e0f7fb] bg-[#eefbff] px-[17px] py-4">
                <div className="flex gap-4">
                  <div className="relative h-20 w-20 min-w-[80px] shrink-0 overflow-hidden rounded-[14px] border border-[#a5f3fc] bg-white p-0.5">
                    {selectedIngredientPreviewUrl ? (
                      <img
                        src={selectedIngredientPreviewUrl}
                        alt=""
                        className="h-full w-full rounded-[12px] object-contain object-center"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center rounded-[12px] bg-[#ecfeff] text-xl font-semibold text-[#22d3ee]">
                        {selectedIngredient.trim().slice(0, 1).toUpperCase() || '?'}
                      </div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-normal leading-[18px] text-[#0e7490]">
                      Live nutrition preview for your selected quantity.
                    </p>
                    <div className="mt-3 flex flex-wrap items-end gap-x-4 gap-y-2">
                      <div className="flex flex-col gap-0.5">
                        <span className="text-xs font-normal leading-[16.5px] tracking-[0.0645px] text-[#9ca3af]">Calories</span>
                        <span className="text-[13px] font-semibold leading-[19.5px] tracking-[-0.0762px] text-[#0a0a0a]">
                          {formatMacro(nutritionData.nutrition.calories)} kcal
                        </span>
                      </div>
                      <div className="flex flex-col gap-0.5">
                        <span className="text-xs font-normal leading-[16.5px] tracking-[0.0645px] text-[#9ca3af]">Protein</span>
                        <span className="text-[13px] font-semibold leading-[19.5px] tracking-[-0.0762px] text-[#0a0a0a]">
                          {formatMacro(nutritionData.nutrition.protein)}g
                        </span>
                      </div>
                      <div className="flex flex-col gap-0.5">
                        <span className="text-xs font-normal leading-[16.5px] tracking-[0.0645px] text-[#9ca3af]">Carbs</span>
                        <span className="text-[13px] font-semibold leading-[19.5px] tracking-[-0.0762px] text-[#0a0a0a]">
                          {formatMacro(nutritionData.nutrition.carbs)}g
                        </span>
                      </div>
                      <div className="flex flex-col gap-0.5">
                        <span className="text-xs font-normal leading-[16.5px] tracking-[0.0645px] text-[#9ca3af]">Fat</span>
                        <span className="text-[13px] font-semibold leading-[19.5px] tracking-[-0.0762px] text-[#0a0a0a]">
                          {formatMacro(nutritionData.nutrition.fat)}g
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end gap-2 border-t border-[#f3f4f6] pt-3">
              <Button 
                size="sm" 
                onClick={handleSaveIngredient} 
                disabled={!canSubmitIngredient}
                className="h-[34px] rounded-[10px] border-0 bg-[#20aedc] px-3 text-[13px] font-medium text-white hover:bg-[#1b9bc4] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isSaving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving...
                  </>
                ) : loadingNutrition ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Fetching Nutrition...
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
              <Button 
                size="sm" 
                variant="outline" 
                onClick={handleCancel}
                disabled={isSaving}
                className="h-[34px] rounded-[10px] border-[#e5e7eb] px-4 text-[13px] font-semibold text-[#374151] hover:bg-[#f9fafb]"
              >
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
            </div>
          </div>
        </div>
      );
    }

    const displayName = formatIngredientDisplayName(ingredient.name);

    return (
      <div
        key={ingredient.id || index}
        className="flex items-center gap-4 rounded-[14px] border border-[#f3f4f6] bg-white px-4 py-3 shadow-sm transition-colors hover:bg-[#fafafa]"
      >
        {ingredient.image && (
          <img 
            src={ingredient.image} 
            alt={displayName}
            className="w-12 h-12 object-cover rounded-lg shadow-sm"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        )}
        <div className="min-w-0 flex-1">
          {(() => {
            const amount = ingredient.amount || 1;
            const formattedAmount = Math.round(amount * 100) / 100;

            return (
              <div className="leading-tight text-[#111827]">
                <span className="text-sm font-medium text-[#6b7280]">
                  {formattedAmount} {ingredient.unit==""?'piece':ingredient.unit}
                </span>
                <span
                  className="block break-words text-[14px] font-semibold text-[#111827] line-clamp-2"
                  title={displayName}
                >
                  {displayName}
                </span>
              </div>
            );
          })()}
        </div>
        {(canEditIngredients && (!shouldLockOriginalIngredientEditing || ingredient.isAdded === true)) && (
          <div className="flex gap-1.5">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => handleEditIngredient(ingredient.id)}
              className="h-8 w-8 rounded-full p-0 text-[#1d9bd1] hover:bg-[#eefbff] hover:text-[#0e7490]"
              title="Edit ingredient"
            >
              <Edit3 className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => handleRemoveIngredient(ingredient.id)}
              disabled={isDeletingAny || isSaving}
              className="h-8 w-8 rounded-full p-0 text-[#f04438] hover:bg-[#fef2f2] hover:text-[#b42318]"
              title={isDeletingAny ? "Please wait for current deletion to complete" : "Remove ingredient"}
            >
              {deletingId === ingredient.id ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
            </Button>
          </div>
        )}
      </div>
    );
  };

  return (
    <Card className="overflow-hidden rounded-2xl border border-[#e5e7eb] bg-white shadow-[0px_8px_20px_-4px_rgba(0,0,0,0.08),0px_4px_8px_-2px_rgba(0,0,0,0.04)]">
      <CardHeader className="border-b border-[#f3f4f6] pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <CardTitle className="text-[18px] font-semibold text-[#111827]">Ingredients</CardTitle>
                <span className="rounded-full bg-[#f3f4f6] px-2 py-0.5 text-xs font-medium text-[#6b7280]">
                  {servingDisplayValue}
                </span>
                <RecipeServingInfoPopover
                  defaultServingId={servingDisplayDefaultId}
                  defaultServingGrams={servingDisplayDefaultGrams}
                  selectedServingId={servingDisplaySelectedId}
                  selectedServingGrams={servingDisplaySelectedGrams}
                  servingSizes={servingDisplaySizes}
                  className="inline-flex shrink-0 items-center text-[#9ca3af] hover:text-[#6b7280]"
                  iconClassName="h-4 w-4"
                />
              </div>
              <p className="text-sm font-normal text-[#6b7280]">
                Ingredients for {servingsForDisplay} {servingsForDisplay === 1 ? 'serving' : 'servings'}
              </p>
            </div>
          </div>
          {canAddIngredients && (
            <Button
              size="sm"
              variant="default"
              onClick={handleAddNew}
              className="h-[34px] gap-1.5 rounded-[10px] border-0 bg-[#20aedc] px-3 text-[13px] font-medium text-white hover:bg-[#1b9bc4]"
            >
              <Plus className="h-4 w-4" />
              Add Ingredient
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="px-5 py-4">
        <div className="space-y-4">
          {ingredientFeedback ? (
            <InlineFeedback
              variant={ingredientFeedback.variant}
              title={ingredientFeedback.title}
              message={ingredientFeedback.message}
            />
          ) : null}
          {/* Add New Ingredient Form - Show at top when adding */}
          {addingNew && (
            <div
              className="flex max-h-[500px] flex-col gap-0 overflow-hidden rounded-2xl bg-white shadow-[0px_8px_20px_-4px_rgba(0,0,0,0.1),0px_4px_8px_-2px_rgba(0,0,0,0.06)] ring-1 ring-slate-200"
              style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
            >
              <div className="shrink-0 border-b border-transparent px-5 pb-3 pt-4">
                <label className="flex h-[38px] cursor-text items-center gap-2 rounded-[14px] border border-[#e5e7eb] bg-white px-[13px] transition-colors focus-within:border-[#d1d5dc]">
                  <Search className="pointer-events-none size-[14px] shrink-0 text-[#99a1af]" strokeWidth={2} aria-hidden />
                  <input
                    type="text"
                    autoComplete="off"
                    enterKeyHint="search"
                    value={searchQuery}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSearchQuery(value);
                      setSelectedIngredient('');
                      setHasUserEditedSearchInEdit(false);
                    }}
                    placeholder="Search by Ingredients"
                    aria-label="Search by ingredients"
                    className="min-h-0 min-w-0 flex-1 border-0 bg-transparent p-0 text-[13px] tracking-[-0.0762px] text-[#1e2939] placeholder:text-[#99a1af] shadow-none outline-none ring-0 focus:border-0 focus:shadow-none focus:outline-none focus:ring-0 focus-visible:border-0 focus-visible:outline-none focus-visible:ring-0"
                  />
                  {isLoadingSearch ? (
                    <Loader2 className="size-4 shrink-0 animate-spin text-[#20aedc]" aria-hidden />
                  ) : null}
                </label>
              </div>

              <div className="shrink-0 px-5 pb-1">
                {!selectedIngredient.trim() &&
                searchQuery.trim().length >= 2 &&
                !isLoadingSearch ? (
                  <p className="mt-2 text-xs leading-[18px] text-[#9ca3af]">
                    {enrichedSearchResults.length === 0
                      ? 'No ingredients found'
                      : `${enrichedSearchResults.length} Ingredients found`}
                  </p>
                ) : null}
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-4 pt-2">
                <div className="flex flex-col gap-4">
                  {selectedIngredient.trim() ? (
                    <>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Quantity</label>
                          <Input
                            id="add-ingredient-quantity"
                            type="number"
                            value={quantity}
                            onChange={(e) => handleQuantityChange(e.target.value)}
                            onWheel={(e) => e.currentTarget.blur()}
                            placeholder="1"
                            min="0"
                            step="0.1"
                            className="h-10 border-slate-200 bg-white focus:border-slate-300 focus:ring-slate-300 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Unit</label>
                          {!canEditUnit ? (
                            <Input
                              id="add-ingredient-unit-readonly"
                              value={unit || (loadingUnits ? 'Loading...' : 'Select ingredient first')}
                              disabled
                              readOnly
                              className="h-10 border-slate-200 bg-slate-50 text-slate-700"
                            />
                          ) : (
                            <Select value={unit} onValueChange={handleUnitChange} disabled={loadingUnits}>
                              <SelectTrigger id="add-ingredient-unit" className="h-10 border-slate-200 bg-white focus:border-slate-300 focus:ring-slate-300">
                                <SelectValue placeholder={loadingUnits ? 'Loading...' : 'Select unit'} />
                              </SelectTrigger>
                              <SelectContent className="z-[9999] w-[var(--radix-select-trigger-width)]" position="popper" side="bottom" align="start" sideOffset={4}>
                                {availableUnits.length > 0 ? (
                                  availableUnits.map((unitOption) => (
                                    <SelectItem key={unitOption} value={unitOption} className="text-xs py-1.5">
                                      {unitOptionLabelsByKey[normalizeUnitKey(unitOption)] ?? formatGramMlWeightLabel(
                                        unitOption,
                                        unitWeightsByKey[normalizeUnitKey(unitOption)],
                                        gramsPerMl,
                                      )}
                                    </SelectItem>
                                  ))
                                ) : (
                                  <SelectItem value="no-units" disabled className="text-xs py-1.5">
                                    No units available
                                  </SelectItem>
                                )}
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                      </div>

                      {loadingNutrition && (
                        <div className="rounded-[14px] border border-[#e0f2fe] bg-[#f0f9ff] p-3">
                          <div className="flex items-center justify-center gap-3 text-[#0369a1]">
                            <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-[#0369a1]"></div>
                            <span className="text-sm font-medium">Fetching nutrition...</span>
                          </div>
                        </div>
                      )}

                      {!loadingNutrition &&
                        nutritionData &&
                        nutritionData.nutrition &&
                        nutritionContext?.ingredient === selectedIngredient &&
                        nutritionContext?.quantity === parseFloat(quantity) && (
                          <div className="rounded-[14px] border border-[#e0f7fb] bg-[#eefbff] px-[17px] py-4">
                            <div className="flex gap-4">
                              <div className="relative h-20 w-20 min-w-[80px] shrink-0 overflow-hidden rounded-[14px] border border-[#a5f3fc] bg-white p-0.5">
                                {selectedIngredientPreviewUrl ? (
                                  <img
                                    src={selectedIngredientPreviewUrl}
                                    alt=""
                                    className="h-full w-full rounded-[12px] object-contain object-center"
                                  />
                                ) : (
                                  <div className="flex h-full w-full items-center justify-center rounded-[12px] bg-[#ecfeff] text-xl font-semibold text-[#22d3ee]">
                                    {selectedIngredient.trim().slice(0, 1).toUpperCase() || '?'}
                                  </div>
                                )}
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="text-xs font-normal leading-[18px] text-[#0e7490]">
                                  Live nutrition preview for your selected quantity.
                                </p>
                                <div className="mt-3 flex flex-wrap items-end gap-x-4 gap-y-2">
                                  <div className="flex flex-col gap-0.5">
                                    <span className="text-xs font-normal leading-[16.5px] tracking-[0.0645px] text-[#9ca3af]">
                                      Calories
                                    </span>
                                    <span className="text-[13px] font-semibold leading-[19.5px] tracking-[-0.0762px] text-[#0a0a0a]">
                                      {formatMacro(nutritionData.nutrition.calories)} kcal
                                    </span>
                                  </div>
                                  <div className="flex flex-col gap-0.5">
                                    <span className="text-xs font-normal leading-[16.5px] tracking-[0.0645px] text-[#9ca3af]">
                                      Protein
                                    </span>
                                    <span className="text-[13px] font-semibold leading-[19.5px] tracking-[-0.0762px] text-[#0a0a0a]">
                                      {formatMacro(nutritionData.nutrition.protein)}g
                                    </span>
                                  </div>
                                  <div className="flex flex-col gap-0.5">
                                    <span className="text-xs font-normal leading-[16.5px] tracking-[0.0645px] text-[#9ca3af]">
                                      Carbs
                                    </span>
                                    <span className="text-[13px] font-semibold leading-[19.5px] tracking-[-0.0762px] text-[#0a0a0a]">
                                      {formatMacro(nutritionData.nutrition.carbs)}g
                                    </span>
                                  </div>
                                  <div className="flex flex-col gap-0.5">
                                    <span className="text-xs font-normal leading-[16.5px] tracking-[0.0645px] text-[#9ca3af]">
                                      Fat
                                    </span>
                                    <span className="text-[13px] font-semibold leading-[19.5px] tracking-[-0.0762px] text-[#0a0a0a]">
                                      {formatMacro(nutritionData.nutrition.fat)}g
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                    </>
                  ) : (
                    <section>
                      {enrichedSearchResults.map((item) => {
                        const rowId = String(item.id ?? item.name);
                        const thumb =
                          typeof item.imageUrl === 'string'
                            ? item.imageUrl
                            : typeof item.image === 'string'
                              ? item.image
                              : undefined;
                        const description = typeof item.description === 'string' ? item.description.trim() : '';
                        return (
                          <div key={rowId} className="border-b border-[#f3f4f6] py-1 last:border-0">
                            <button
                              type="button"
                              onClick={() => void handleSelectIngredient(item)}
                              className="flex w-full items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-slate-50"
                              aria-label={`Select ${item.name}`}
                            >
                              <AddIngredientResultThumbnail imageUrl={thumb} name={item.name} />
                              <div className="min-w-0 flex-1">
                                <p className="truncate text-[13px] font-semibold leading-4 tracking-[-0.0762px] text-[#1e2939]">
                                  {item.name}
                                </p>
                                {item.aisle ? (
                                  <p className="mt-0.5 truncate text-xs text-slate-500">{item.aisle}</p>
                                ) : null}
                                {description ? (
                                  <p className="mt-0.5 line-clamp-2 text-xs leading-4 text-slate-500">
                                    {description}
                                  </p>
                                ) : null}
                                <RecipeAllergenCard
                                  allergens={item.allergens}
                                  compact
                                  maxVisible={3}
                                  className="mt-1"
                                />
                                <SpoonacularTagSummary item={item} className="mt-1" />
                              </div>
                              <div className="flex h-7 items-center justify-center gap-1.5 rounded-full bg-[#eefbff] px-3 text-xs font-semibold text-[#0e7490] transition-colors">
                                Select
                              </div>
                            </button>
                          </div>
                        );
                      })}

                      {!selectedIngredient.trim() &&
                      enrichedSearchResults.length === 0 &&
                      !isLoadingSearch ? (
                        <div className="rounded-[14px] border border-dashed border-[#e5e7eb] bg-[#fafafa] px-4 py-8 text-center text-sm text-[#9ca3af]">
                          {searchQuery.trim().length < 2
                            ? 'Type at least 2 characters to search ingredients.'
                            : 'No matches. Try a different search.'}
                        </div>
                      ) : null}
                    </section>
                  )}
                </div>
              </div>

              <div className="shrink-0 border-t border-[#f3f4f6] bg-slate-50 px-5 py-3">
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={handleCancel}
                    disabled={isSaving}
                    className="h-[34px] rounded-[10px] border-[#e5e7eb] px-4 text-[13px] font-semibold text-[#374151] hover:bg-[#f9fafb]"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleSaveIngredient}
                    disabled={!canSubmitIngredient}
                    className="h-[34px] gap-1.5 rounded-[10px] border-0 bg-[#20aedc] px-3 text-[13px] font-medium text-white hover:bg-[#1b9bc4] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {isSaving ? (
                      <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-white" />
                    ) : loadingNutrition ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <Check className="size-4" strokeWidth={2.5} />
                    )}
                    {isSaving ? 'Adding...' : 'Add Ingredient'}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Existing Ingredients List */}
          {quantityOnlyMode
            ? editedIngredients.map((ingredient, index) => renderIngredientItem(ingredient, index))
            : (
              <>
                {editingId
                  ? editedIngredients
                    .filter((ingredient) => ingredient.id === editingId)
                    .map((ingredient, index) => renderIngredientItem(ingredient, index))
                  : null}
                {listItems.filter((item) => item.id !== editingId).length > 0 ? (
                  <IngredientList
                    ingredients={listItems.filter((item) => item.id !== editingId)}
                    unitLabelsByKey={unitOptionLabelsByKey}
                    onEdit={(ingredient) => {
                      void handleEditIngredient(ingredient.id);
                    }}
                    onRemove={(id) => {
                      void handleRemoveIngredient(id);
                    }}
                  />
                ) : (
                  !addingNew && (
                    <div className="rounded-[14px] border border-dashed border-[#e5e7eb] bg-[#fafafa] px-4 py-8 text-center text-sm text-[#9ca3af]">
                      No ingredients added yet. Use "Add Ingredient" to start.
                    </div>
                  )
                )}
              </>
            )}
        </div>
      </CardContent>
    </Card>
  );
};

export default IngredientsTab;
